import { Container, Button, Row, Col } from "reactstrap";
import React, { useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RETENTION_PATHS } from "../../../../Routes/paths";
import { useRetentionService } from "../api/useRetentionService";
import { formatDate } from "../../../../common/service/CommonService";
import SectionCard from "Components/Common/SectionCard";
import FullPageLoader from "Components/Common/FullPageLoader";
import DetailField from "../../../../Components/Common/DetailField";
import { BreadcrumbContext } from "Layouts/Header";

const RetentionView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const breadcrumbContext = useContext(BreadcrumbContext);

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Drug Retention", link: RETENTION_PATHS.LIST },
      { text: "View Retention Record" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, []);

  const { SelectIdQuery } = useRetentionService();
  const selectQuery = SelectIdQuery(id!);

  let data = selectQuery.data?.data?.[0];

  if (selectQuery.isLoading) {
    return <FullPageLoader />;
  }

  if (!data) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <i className="ri-error-warning-line display-3 text-warning mb-3 d-block" />
            <h4 className="fw-600">Record Not Found</h4>
            <p className="text-muted">The retention record you are looking for does not exist.</p>
            <Button color="primary" onClick={() => navigate(RETENTION_PATHS.LIST)}>
              Back to List
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  /* ── Category Badge ── */
  const categoryColors: Record<string, { bg: string; text: string; border: string }> = {
    "Test Product": { bg: "bg-primary-subtle", text: "text-primary", border: "border-primary" },
    "Reference Product": { bg: "bg-info-subtle", text: "text-info", border: "border-info" },
    Placebo: { bg: "bg-warning-subtle", text: "text-warning", border: "border-warning" },
    "Ancillary / Supply": { bg: "bg-secondary-subtle", text: "text-secondary", border: "border-secondary" },
  };
  const catStyle = categoryColors[data.ipcategory] || categoryColors["Test Product"];
  const categoryBadge = (
    <span
      className={`badge ${catStyle.bg} ${catStyle.text} px-3 py-2 rounded-pill fs-13 border ${catStyle.border}`}
    >
      {data.ipcategory || "—"}
    </span>
  );

  return (
    <div className="page-content">
      <Container fluid>
        {/* ── Header ── */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 className="fw-700 mb-1">Retention Record Details</h3>
            <p className="text-muted mb-0">
              Record ID: RET-{data.retentionid ?? id}&nbsp;|&nbsp;
              Study: {data.studycode}&nbsp;|&nbsp;
              Product: {data.productname}
            </p>
          </div>

        </div>

        <div className="d-flex flex-column gap-2">
          {/* 1 — Study & Product Details */}
          <SectionCard
            icon="ri-microscope-line"
            title="Study & Product Details"
            subtitle="Study protocol and investigational product information."
          >
            <Row>
              <DetailField label="Study Code" value={data.studycode} columns={3} />
              <DetailField label="Sponsor Name" value={data.sponsorname} columns={3} />
              <Col md={3} className="mb-3">
                <label className="text-muted fs-12 mb-1 text-uppercase fw-600 ls-1 d-block">IP Category</label>
                <div style={{ minHeight: "24px" }}>{categoryBadge}</div>
              </Col>
              <DetailField label="Product Name" value={data.productname} columns={3} />
              <DetailField label="Strength" value={data.strength} columns={3} />
              <DetailField label="Dosage Form" value={data.dosageform} columns={3} />
              <DetailField label="Batch / Lot No." value={data.batchlotno} columns={3} />
              <DetailField
                label="Required Storage Condition"
                value={data.storageconditiontext}
                columns={3}
              />
            </Row>
          </SectionCard>

          {/* 2 — Retention Details */}
          <SectionCard
            icon="ri-archive-line"
            title="Retention Details"
            subtitle="Quantities and archival category."
          >
            <Row>
              <DetailField label="Total Quantity Received" value={data.ipquantity} columns={3} />
              <DetailField label="Quantity Allocated for Retention" value={data.retentionquantity} columns={3} />
              <DetailField label="Retention Type" value={data.retentiontypetext} columns={3} />
            </Row>
          </SectionCard>

          {/* 3 — Timeline */}
          <SectionCard
            icon="ri-calendar-event-line"
            title="Retention Timeline"
            subtitle="Archival period and automated expiry tracking."
          >
            <Row>
              <DetailField label="Retention Start Date" value={formatDate(data.retentionstartdate)} columns={3} />
              <DetailField label="Retention Expiry Date" value={formatDate(data.retentionexpirydate)} columns={3} />
              <DetailField label="Retention Period" value={data.retentionperiod} columns={3} />
            </Row>
          </SectionCard>
        </div>
      </Container>
    </div>
  );
};

export default RetentionView;
